import { ConfigurationToName, DocumentationCardItem } from './types';
import { Fragment, useMemo } from 'react';
import { DialogLoader } from '../../common/dialogs/DialogLoader';
import Box from '@mui/material/Box';
import { DefaultConfigurationTable } from '../configuration-item-tables/DefaultConfigurationTable';
import { FieldsTable } from '../configuration-item-tables/FieldsTable';
import { FlowsTable } from '../configuration-item-tables/FlowsTable';
import { ObjectsTable } from '../configuration-item-tables/ObjectsTable';
import { RelatedObjectsTable } from '../configuration-item-tables/RelatedObjectsTable';
import { PossibleSectionInDependencies } from '../building-blocks/documentation-card/search/types';
import {
  SalesforceItem,
  SalesforceItemField,
  SalesforceItemRollup,
  ROLLUP,
} from '../../parser/ParserTypes';
import { FIELD, FLOW, LAYOUT, OBJECT } from '@server/parser';

export interface DependenciesTableMap {
  [key: string]: SalesforceItem[];
}

interface DependenciesTableProps {
  configurationItems: DependenciesTableMap;
  onTableItemClick: (props: DocumentationCardItem) => void;
  isLoadingDependency?: boolean;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

export const DependenciesTable = ({
  configurationItems,
  onTableItemClick,
  isLoadingDependency,
  activeItemId,
  clearHistory,
  section = 'dependencies',
}: DependenciesTableProps) => {
  const configurationKeys = useMemo(
    () => (configurationItems ? Object.keys(configurationItems) : []),
    [configurationItems],
  );

  const renderedTables = useMemo(() => {
    if (!configurationItems) {
      return [];
    }
    return configurationKeys.map((confKey, idx) => {
      const key = confKey as string;
      const items = configurationItems[key];

      if (!items.length) {
        return <Fragment key={`no-configuration-items_${idx}`} />;
      }

      const createTableContent = () => {
        switch (key) {
          case FIELD: {
            return (
              <FieldsTable
                key={key}
                onFieldClick={onTableItemClick}
                items={items as SalesforceItemField[]}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Fields"
                dataTableType={FIELD}
                section={section}
              />
            );
          }

          case ROLLUP: {
            return (
              <FieldsTable
                key={key}
                onFieldClick={onTableItemClick}
                items={items as SalesforceItemRollup[]}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Rollups"
                dataTableType={ROLLUP}
                section={section}
              />
            );
          }

          case LAYOUT:
            return (
              <DefaultConfigurationTable
                tableHeader={ConfigurationToName[key]}
                items={items}
                onItemClick={onTableItemClick}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case FLOW:
            return (
              <FlowsTable
                key={key}
                items={items}
                onFlowClick={onTableItemClick}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case 'relatedObjects':
            return (
              <RelatedObjectsTable
                key={key}
                items={items}
                onItemClick={onTableItemClick}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case OBJECT:
            return (
              <ObjectsTable
                key={key}
                items={items.map((item) => ({
                  id: item.metadataId,
                  name: item.name,
                  label: item.label,
                  metadataId: item.metadataId,
                }))}
                onItemClick={onTableItemClick}
                clearHistory={clearHistory}
                section={section}
              />
            );

          default:
            return (
              <DefaultConfigurationTable
                tableHeader={ConfigurationToName[key] ?? key}
                items={items}
                onItemClick={onTableItemClick}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );
        }
      };

      const tableContent = createTableContent();

      return <Fragment key={key}>{tableContent}</Fragment>;
    });
  }, [
    configurationKeys,
    configurationItems,
    onTableItemClick,
    activeItemId,
    clearHistory,
    section,
  ]);

  if (!configurationItems) {
    return <></>;
  }

  return (
    <Box
      sx={{
        '& .MuiTableBody-root .MuiTableRow-root': {
          '& .chevron-button': {
            opacity: 0,

            '& > .MuiButton-root': {
              minWidth: 0, //to fix table overflow
            },
          },

          '&:hover .chevron-button': {
            opacity: 1,
          },
        },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <DialogLoader visible={isLoadingDependency} variant="light" />
      {renderedTables}
    </Box>
  );
};
